
/// <amd-module name="Wit/BoardsHub/KanbanBoard/SignalR/KanbanBoardSignalRHub" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssHistoryService } from "VSS/Platform/History";
import { IHubHostInformation, SingleConnectionSignalRHub } from "VSS/Features/SignalR/Hub";
import { ISignalRHubProxy } from "VSS/Features/SignalR/HubProxies.types";
import { BoardWorkItemsUpdatedCallback, BoardSettingsUpdatedCallback } from "./Contracts";

export interface IKanbanBoardSignalROptions {
    hostInformation: IHubHostInformation;
    historyService: IVssHistoryService;
    pageContext: IVssPageContext;
    useSignalRAppPoolDefaultValue?: boolean;
}

const UserInactivityTimeoutMs = 60 * 60 * 1000; // 1 hour
const TimeoutDelayMs = 30000;

interface IKanbanBoardHandlers {
    onWorkItemsUpdated: BoardWorkItemsUpdatedCallback;
    onCommonSettingsChanged: BoardSettingsUpdatedCallback;
}

interface IKanbanBoardHubClient extends IKanbanBoardHandlers {}

interface IKanbanBoardHubServer {
    watchKanbanBoard: (boardExtensionId: string) => Promise<void>;
    unwatchKanbanBoard: (boardExtensionId: string) => Promise<void>;
}

function getProxyToRegister(
    baseProxy: ISignalRHubProxy<IKanbanBoardHubClient, IKanbanBoardHubServer>
): ISignalRHubProxy<IKanbanBoardHubClient, IKanbanBoardHubServer> {
    baseProxy.server = {
        unwatchKanbanBoard: (boardExtensionId: string) => baseProxy.invoke.apply(baseProxy, ["UnwatchKanbanBoard", boardExtensionId]),
        watchKanbanBoard: (boardExtensionId: string) => baseProxy.invoke.apply(baseProxy, ["WatchKanbanBoard", boardExtensionId])
    };

    return baseProxy;
}

export class KanbanBoardSignalRHub extends SingleConnectionSignalRHub<IKanbanBoardHubClient, IKanbanBoardHubServer> {
    private static Name = "kanbanBoardHub";

    private timeoutHandle: number | null = null;
    private handlers: IKanbanBoardHandlers | null = null;
    private boardExtensionId: string | null = null;

    private get hub() {
        return this.getHub(KanbanBoardSignalRHub.Name);
    }

    constructor(options: IKanbanBoardSignalROptions) {
        super({
            name: KanbanBoardSignalRHub.Name,
            userInactivityTimeoutMs: UserInactivityTimeoutMs,
            proxyToRegister: getProxyToRegister,
            removeApiPath: true,
            ...options
        });
        this.initializeProxyClient(KanbanBoardSignalRHub.Name, proxy => this._initializeClient(proxy));
    }

    public async watch(
        boardExtensionId: string,
        onWorkItemsUpdated: BoardWorkItemsUpdatedCallback,
        onCommonSettingsChanged: BoardSettingsUpdatedCallback
    ) {
        if (this.boardExtensionId && this.boardExtensionId !== boardExtensionId) {
            await this.endWatch(this.boardExtensionId);
        }

        this.boardExtensionId = boardExtensionId;
        this.handlers = { onWorkItemsUpdated, onCommonSettingsChanged };

        try {
            await this.start();
            await this.hub?.server.watchKanbanBoard(boardExtensionId);
        } catch (e: unknown) {
            this._stopOnTimeout();
            this.handlers = null;
            throw new Error("Not initialized");
        }
    }

    public async endWatch(boardId: string) {
        if (boardId !== this.boardExtensionId) {
            return;
        }

        this._clearTimeout();
        this.boardExtensionId = null;
        this.handlers = null;

        await this.hub?.server.unwatchKanbanBoard(boardId);
    }

    /**
     * Initialize the hub.
     */
    private _initializeClient(proxy: ISignalRHubProxy<IKanbanBoardHubClient, IKanbanBoardHubServer>) {
        proxy.client.onWorkItemsUpdated = updates => this.handlers?.onWorkItemsUpdated(updates);
        proxy.client.onCommonSettingsChanged = () => this.handlers?.onCommonSettingsChanged();
    }

    private _stopOnTimeout() {
        this.timeoutHandle = setTimeout(() => this.stop(), TimeoutDelayMs);
    }

    private _clearTimeout(): void {
        if (this.timeoutHandle) {
            clearTimeout(this.timeoutHandle);
            this.timeoutHandle = null;
        }
    }
}
